import React, { useState } from 'react';

const BLUE = '#3a6ad9';

// Eva's avatar, shared by every surface she speaks on (the chat widget and the
// message threads). Renders the avatar when present; falls back to a clean "E"
// monogram on a blue circle otherwise.
//
// This was previously a private function inside DoorlyChat.jsx. It lives here so
// the widget and the thread renderer cannot drift apart — one Eva, one face.
//
// ?v= IS LOAD-BEARING, do not strip it. Unlike the JS/CSS bundles, files in
// public/ are NOT content-hashed — the URL is identical on every deploy — and
// service-worker.js serves anything matching /\.(png|ico|svg|webp)$/ CACHE-FIRST
// with no expiry. A browser that requested this path before the file was
// deployed cached the miss and, being cache-first, never re-asked the network:
// the JS updated (new hashed filename) while the image stayed stale forever.
// The query string makes each revision a distinct cache key. BUMP IT whenever
// eva-avatar.png is replaced, or the new face will not reach existing users.
// ⚠️ THE 120px FILE, NOT THE 1024px ORIGINAL. eva-avatar.png is 1,507,977 bytes
// and was being fetched on every app load to render a 36px circle — roughly 200x
// the pixels needed. eva-avatar-120.png is 6,979 B, 3x the largest render, and
// keeps the alpha-0 corners the cutout depends on (verified, not assumed).
//
// Switching PATH rather than bumping ?v= also sidesteps the cache-first service
// worker described below by construction: a new path is a new cache key, so no
// existing client can be holding a stale entry for it.
const AVATAR_SRC = '/eva-avatar-120.png?v=1';

// eva-avatar.png is a CUTOUT — its corner pixels are alpha 0, so whatever sits
// behind the <img> is what shows around Eva. This fill is therefore the visible
// circle colour, not a placeholder that only appears while loading.
const AVATAR_BG = '#fff';

export function EvaAvatar({ size = 36 }) {
  const [failed, setFailed] = useState(false);
  const base = { width: size, height: size, borderRadius: '50%', flexShrink: 0 };
  if (failed) {
    return (
      <div style={{ ...base, background: BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#fff', fontWeight: 800, fontSize: Math.round(size * 0.5), lineHeight: 1 }}>E</span>
      </div>
    );
  }
  return (
    <img src={AVATAR_SRC} alt="Eva" onError={() => setFailed(true)}
      style={{ ...base, objectFit: 'cover', background: AVATAR_BG }} />
  );
}

// Eva's byline under a message-thread reply. Deliberately mirrors the LAYOUT of
// MessageSignature (28px circle + name + sublabel) so her replies sit naturally
// beside a team member's — but never its CONTENT: no job title, and never an
// NMLS number. The "Doorly's AI assistant" line is not decoration, it is the
// attribution: on a channel that has only ever been human-to-human, a borrower
// must never have to infer that they are reading a bot.
// Per-answer helpfulness control. ALWAYS VISIBLE, never hover-revealed: this app
// ships as a Capacitor native build, and on touch there is no hover state — a
// hover-only control would be permanently invisible to most of the people we are
// trying to collect the signal from. Instead it is always mounted but visually
// quiet (small, low-contrast, no fill until chosen), and rendered ONLY under
// Eva's substantive answers — never the user's own messages, the opening
// greeting, or an error message.
//
// `rating` is 1 | -1 | null. onRate(next) is called with the thumb that was
// clicked; the caller decides whether that sets or clears.
export function EvaFeedback({ rating, onRate }) {
  const Thumb = ({ value, label, down }) => {
    const active = rating === value;
    return (
      <button
        type="button"
        onClick={() => onRate(value)}
        aria-label={label}
        aria-pressed={active}
        title={label}
        style={{
          background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer',
          lineHeight: 0, opacity: active ? 1 : 0.45, transition: 'opacity 0.12s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = active ? 1 : 0.45; }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24"
          fill={active ? BLUE : 'none'} stroke={active ? BLUE : '#6b7280'} strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
          style={down ? { transform: 'rotate(180deg)' } : undefined}>
          <path d="M7 10v12" />
          <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
        </svg>
      </button>
    );
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 3, marginLeft: 2 }}>
      <Thumb value={1} label="This was helpful" />
      <Thumb value={-1} label="This was not helpful" down />
    </div>
  );
}

// The resolution turn, on the message-thread surface. TWO BUTTONS, never free
// text: "Ok ty" appears hundreds of times in this channel's history and is
// unclassifiable as resolution versus politeness, so the answer has to be a
// click. This is one control doing two jobs — it is the helpfulness signal AND,
// via "I still need help", a consent-gated escalation trigger, which satisfies
// the OFFERING A PERSON guardrail without spending a conversational turn.
//
// resolution is 'helped' | 'still_need_help' | null. Once set the buttons are
// replaced by a plain acknowledgement — never re-askable, never nagging.
export function EvaResolution({ resolution, ticketNumber, onResolve, busy }) {
  if (resolution === 'helped') {
    return <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>Thanks — glad that helped.</div>;
  }
  if (resolution === 'still_need_help') {
    return (
      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
        A Doorly team member will reach out within 1 business day.
        {ticketNumber ? ` Your reference number is ${ticketNumber}.` : ''}
      </div>
    );
  }
  const btn = (primary) => ({
    padding: '5px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700,
    cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
    border: `1px solid ${primary ? '#3a6ad9' : '#d1d5db'}`,
    background: primary ? '#3a6ad9' : '#fff',
    color: primary ? '#fff' : '#374151',
  });
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
      <span style={{ fontSize: 11, color: '#6b7280', marginRight: 2 }}>Did that answer it?</span>
      <button type="button" disabled={busy} style={btn(true)} onClick={() => onResolve('helped')}>That helped</button>
      <button type="button" disabled={busy} style={btn(false)} onClick={() => onResolve('still_need_help')}>I still need help</button>
    </div>
  );
}

export function EvaSignature() {
  return (
    <div style={{
      marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(0,0,0,0.08)',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <EvaAvatar size={28} />
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', lineHeight: 1.3 }}>Eva</div>
        <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.3 }}>Doorly&rsquo;s AI Assistant</div>
      </div>
    </div>
  );
}
